import { useState, useEffect, useCallback } from "react";
import {
  filterAssignableUsers,
  determineEffectiveRegion,
  normalizeRole
} from "../../../utils/assignmentRules";
import api from "../../../services/api";
import { useSnackbar } from "../../../components/common/Snackbar";
import axios from "axios";
import { WebConfig } from "../../../web-config";
/**
 * Hook to manage users data for RaiseQueryForm
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isActive - Whether form is active
 * @param {Object} params.currentUser - Current user object
 * @param {string} params.region - Selected region from form
 * @returns {Object} Users state and handlers
 */
export const useRaiseQueryUsers = ({ isActive, currentUser, region }) => {
  const { showSnackbar } = useSnackbar();

  const [users, setUsers] = useState([]);
  const [allAssignableUsers, setAllAssignableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const applyAssignableFilter = useCallback((pool, actor, selectedRegion) => {
    if (!actor) {
      setUsers([]);
      return;
    }

    const role = normalizeRole(actor.role);
    const normalizedSelectedRegion = (selectedRegion || "").trim();

    // For MP role, still require region selection
    if (role === "MP" && !normalizedSelectedRegion) {
      setUsers([]);
      return;
    }

    // For CH role, allow showing users even without region selection (MP users with region 'ALL' will be shown)
    const effectiveRegion = determineEffectiveRegion({
      assignor: actor,
      selectedRegion: normalizedSelectedRegion || undefined
    });

    const filtered = filterAssignableUsers(pool, actor, {
      queryRegion: effectiveRegion
    });

    setUsers(
      filtered.slice().sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base"
        })
      )
    );
  }, []);

  const fetchUsers = useCallback(
    async (userForFiltering, selectedRegion) => {
      setLoadingUsers(true);
      try {
        const currentUserData =
          userForFiltering ||
          currentUser ||
          (typeof window !== "undefined"
            ? JSON.parse(localStorage.getItem("user"))
            : null);

        if (!currentUserData) {
          showSnackbar("User information not found", "error");
          setUsers([]);
          return;
        }

        const response = await api.get("/admin/users", {
          params: { limit: 100, isActive: true }
        });

        const allUsers = response.data?.data?.users || [];

        setAllAssignableUsers(allUsers);
        applyAssignableFilter(allUsers, currentUserData, selectedRegion);
      } catch (error) {
        showSnackbar("Failed to load users", "error");
        // console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    },
    [applyAssignableFilter, showSnackbar, currentUser]
  );

  // Filter users when region changes
  useEffect(() => {
    if (!isActive) return;
    applyAssignableFilter(allAssignableUsers, currentUser, region);
  }, [
    isActive,
    allAssignableUsers,
    currentUser,
    region,
    applyAssignableFilter
  ]);

  // Initial users fetch
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const storedUser =
      currentUser ||
      (typeof window !== "undefined"
        ? JSON.parse(localStorage.getItem("user"))
        : null);
    const regionForFetch = region;
    fetchUsers(storedUser, regionForFetch);
    // Intentionally depend only on `isActive` to avoid refetch loops when state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const fetchProjectUsers = useCallback(
    async (projectCode) => {
      if (!projectCode) {
        setProjectUsers([]);
        return;
      }

      setLoadingUsers(true);

      try {
        const currentUserData =
          currentUser ||
          (typeof window !== "undefined"
            ? (() => {
                const data = localStorage.getItem("user");
                return data ? JSON.parse(data) : null;
              })()
            : null);

        if (!currentUserData) return;

        const response = await axios.get(
          `${WebConfig.REACT_APP_AAI_SSO_BASE_URL}/SEPL.PWIMS.AppServices/ProjectTarget/ProjectTarget.svc/GetProjectAssignee`,
          {
            params: {
              ProjectCode: projectCode,
              UserName: JSON.parse(localStorage.getItem("user"))?.name,
              orgId: 2
            },
            withCredentials: false
          }
        );

        const apiData = response?.data?.data || [];

        // 🔥 flatten response
        const flattened = apiData.flatMap((group) =>
          (group.Value || []).map((user) => ({
            ...user,
            role: group.Key
          }))
        );

        setProjectUsers(flattened);
      } catch (error) {
        setProjectUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    },
    [currentUser]
  );

  return {
    users,
    projectUsers,
    allAssignableUsers,
    loadingUsers,
    fetchProjectUsers,
    fetchUsers
  };
};
